import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Plus, Trash2, Eye, Loader2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BizPlanDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = localStorage.getItem('careerboost_token');
        const response = await axios.get(
          `${BACKEND_URL}/api/bizplan/plans`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPlans(response.data.plans);
      } catch (error) {
        console.debug('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      const token = localStorage.getItem('careerboost_token');
      await axios.delete(
        `${BACKEND_URL}/api/bizplan/plans/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlans(plans.filter(p => p.id !== planId));
      toast.success('Plan deleted');
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/bizplanai');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/bizplanai" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                BizPlanAI
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">Hi, {user?.name?.split(' ')[0]}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Business Plans
            </h1>
            <p className="text-slate-600 mt-1">
              Create and manage your AI-generated business plans
            </p>
          </div>
          <Link to="/bizplanai/generate">
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-plan-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Plan
            </Button>
          </Link>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : plans.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card key={plan.id} className="hover:shadow-lg transition-shadow" data-testid={`plan-card-${plan.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      plan.plan_type === 'detailed' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {plan.plan_type === 'detailed' ? 'Detailed' : 'Basic'}
                    </span>
                  </div>
                  <CardTitle className="text-lg mt-3">{plan.business_name}</CardTitle>
                  <p className="text-sm text-slate-500">{plan.industry}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400 mb-4">
                    Created {formatDate(plan.created_at)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/bizplanai/plans/${plan.id}`)}
                      data-testid={`view-plan-${plan.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(plan.id)}
                      data-testid={`delete-plan-${plan.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No business plans yet</h2>
            <p className="text-slate-600 mb-6">Create your first AI-generated business plan</p>
            <Link to="/bizplanai/generate">
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-first-plan-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Plan
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default BizPlanDashboard;
